/**
 * @fileoverview Focus Component
 * 
 * Displays a focused view of a single proctoring session in a drawer.
 * Provides full access to session details, chat, settings, and timeline.
 * 
 * @module vtc/components/Focus
 */

import { View, Heading, Text, Button, Flex } from '@smarterservices/smarter-ui';
import Card from '../Common/Card';
import FocusHeader from './FocusHeader';
import ConfirmReviewExitModal from '../Modal/ConfirmReviewExit';
import { useFocusState } from './useFocusState';
import { SessionViewerPortal } from '../../contexts/SessionViewerContext';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import { useState } from 'react';
/**
 * Focus Component
 * 
 * Renders a detailed view of a focused session with full SessionViewer capabilities.
 * Displays in a drawer and includes exit confirmation for sessions in review.
 * 
 * **Features:**
 * - Full SessionViewer with all UI components enabled
 * - Student name in header
 * - Action menu (transfer, end session)
 * - Exit confirmation for review sessions
 * - Reuses LiveKit connection from card view
 * - Claim failure UI when session claim is lost or times out
 * 
 * **Claim Failure Handling:**
 * When multiple proctors attempt to claim the same session, only one succeeds.
 * This component displays a failure UI with clear messaging and recovery options:
 * - Lost Race: Shows which proctor claimed the session
 * - Timeout: Indicates verification failure due to network/sync issues
 * - Recovery: "Get Next Session" or "Close" buttons for user action
 * 
 * @component
 * @returns {JSX.Element} The focus view component or claim failure UI
 * 
 * @example
 * // Used in drawer when session is focused
 * <Focus />
 */
function Focus() {
  // Use custom hook to manage all state and logic
  const {
    focusedSession,
    focusedSessionSid,
    showExitConfirmation,
    handleTransfer,
    handleEndSession,
    handleClose,
    handleIncidentReport,
    handleConfirmedExit,
    handleCancelExit,
  } = useFocusState();

  // CLAIM FAILURE STATE: Get claim failure details from session store
  // This is set by setFocusedSession when claim verification fails
  const claimFailure = useSessionStore((state) => state.claimFailure);
  const clearClaimFailure = useSessionStore((state) => state.clearClaimFailure);
  const closeDrawer = useAppStore((state) => state.closeDrawer);
  const openNextCheckinSession = useSessionStore((state) => state.openNextCheckinSession);

  /**
   * Handle closing drawer and clearing failure state
   * User clicked "Close" button in failure UI
   */
  const handleClaimFailureClose = async () => {
    clearClaimFailure(); // Clear failure state first
    await closeDrawer(); // Then close drawer
  };

  /**
   * Handle trying to get next session after failure
   * User clicked "Get Next Session" button in failure UI
   * Clears failure state and attempts to claim next available session
   */
  const handleTryNext = async () => {
    clearClaimFailure(); // Clear failure state first
    await openNextCheckinSession(); // Then try to get next session
  };

  // CLAIM FAILURE UI: If there's a claim failure, show failure UI instead of session viewer
  // This provides clear feedback when session claim is lost to another proctor or times out
  if (claimFailure) {
    // Determine failure type and appropriate explanation message
    const isTimeout = claimFailure.isTimeout;
    const explanationText = isTimeout
      ? 'The session claim could not be verified in time. This may be due to network issues or the session may have been claimed by another proctor.'
      : 'Another proctor claimed this session while you were trying to access it. This can happen when multiple proctors click on the same session simultaneously.';

    return (
      <View
        as="div"
        position="relative"
        id="FocusModeContainer"
        width="100%"
        height="100%"
        margin="none"
        padding="medium"
        overflowY="auto"
        withVisualDebug={false}
      >
        <Card>
          <Card.Body>
            <Flex direction="column" gap="medium" alignItems="center" justifyItems="center" padding="large">
              <Heading level="h2" margin="none">
                Session Unavailable
              </Heading>

              <Text size="large" textAlign="center">
                {claimFailure.message}
              </Text>

              <Text size="medium" color="secondary" textAlign="center">
                {explanationText}
              </Text>

              <Flex gap="small" margin="medium 0 0 0">
                <Button onClick={handleTryNext} color="primary">
                  Get Next Session
                </Button>
                <Button onClick={handleClaimFailureClose}>
                  Close
                </Button>
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </View>
    );
  }

  return (
    <>
      <View
        as="div"
        position="relative"
        id="FocusModeContainer"
        width="100%"
        height="100%"
        margin="none"
        padding="xxx-small"
        overflowY="hidden"
        withVisualDebug={false}
      >
        <Card>
          {/* Header with student name and action buttons */}
          <FocusHeader
            focusedSession={focusedSession}
            onTransfer={handleTransfer}
            onEndSession={handleEndSession}
            onClose={handleClose}
            onIncidentReport={handleIncidentReport}
          />

          {/* Body - Viewer is moved here via DOM manipulation */}
          <Card.Body overflowX="visible">
            <SessionViewerPortal sessionSid={focusedSessionSid} isFocused={true} />
          </Card.Body>
        </Card>

        {/* Exit confirmation modal for review sessions */}
        <ConfirmReviewExitModal
          show={showExitConfirmation}
          onCancel={handleCancelExit}
          onConfirmation={handleConfirmedExit}
        />

      </View>


    </>
  );
}

export default Focus;
